import axios from 'axios';
import { i18n } from '@/to-fsd/shared/lib/i18n';
import { refreshToken as apiRefreshToken } from '@/to-fsd/shared/lib/keycloak/api/endpoints';
import { useUserStore } from '@/to-fsd/shared/store/user';
import { useNasiyaGlobalStore } from '@/to-fsd/shared/store/nasiya-global';
import { useMainStore } from '@/to-fsd/shared/store/main';
import { router } from '@/to-fsd/app';
import { ROUTE_NAMES } from '@/to-fsd/shared/constants';
import { AtToastPlugin } from '@packages/aliftech-ui';
// import AxiosMockAdapter from 'axios-mock-adapter';

export const apiClient = new axios.create({
  baseURL: `${process.env.VUE_APP_BACKEND}`,
});

// const mock = new AxiosMockAdapter(apiClient, { onNoMatch: 'passthrough', delayResponse: 500 });
//
// mock.onPost('/merchant/card/cards/request-attach').reply(400, {
//   code: 'NOT_HAVE_SMS_INFO',
//   message: 'not found hello',
// });

// mock.onPost('/merchant/warehouse/items/delete').reply(404, {
//   message: 'Hello World',
// });

apiClient.interceptors.request.use(async config => {
  try {
    config.headers['locale'] = i18n?.global?.locale || 'uz';
    config.headers['Apikey'] = process.env.VUE_APP_KEY_CLOAK_API_KEY;

    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const hasAccessToken = Boolean(accessToken);
    const hasRefreshToken = Boolean(refreshToken);

    const isLoggedIn = hasAccessToken && hasRefreshToken;

    if (isLoggedIn) {
      const accessTokenExpire = Number(localStorage.getItem('access_token_expire') || 0);
      const refreshTokenExpire = Number(localStorage.getItem('refresh_token_expire') || 0);
      let now = Math.floor(Date.now() / 1000);
      const isAccessTokenExpired = now >= accessTokenExpire;
      const isRefreshTokenExpired = now >= refreshTokenExpire;

      if (isAccessTokenExpired) {
        if (isRefreshTokenExpired) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('access_token_expire');
          localStorage.removeItem('refresh_token_expire');
        } else {
          const res = await apiRefreshToken({ refreshToken });

          const user = useUserStore();

          await user.fetchUser();

          localStorage.setItem('access_token', res.data.access_token);
          localStorage.setItem('refresh_token', res.data.refresh_token);

          now = Math.floor(Date.now() / 1000);
          const accessTokenExpiresIn = now + res.data.expires_in - 5;
          const refreshTokenExpiresIn = now + res.data.refresh_expires_in - 5;

          localStorage.setItem('access_token_expire', accessTokenExpiresIn);
          localStorage.setItem('refresh_token_expire', refreshTokenExpiresIn);
        }
      }

      config.headers['Authorization'] = 'Bearer ' + localStorage.getItem('access_token');
    }

    return config;
  } catch {
    const user = useUserStore();
    const globalStore = useNasiyaGlobalStore();
    const mainStore = useMainStore();

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('access_token_expire');
    localStorage.removeItem('refresh_token_expire');

    user.$reset();
    globalStore.$reset();
    mainStore.$reset();

    router.replace({ name: ROUTE_NAMES.login });

    AtToastPlugin.error({
      title: '[KC]' + i18n.global.t('app.loginAgainRequired'),
      duration: 4000,
    });
  }
});

apiClient.interceptors.response.use(
  res => res,
  async e => {
    const status = e.response?.status;

    if (status === 401) {
      router.replace({ name: ROUTE_NAMES.login });

      const user = useUserStore();
      const globalStore = useNasiyaGlobalStore();
      const mainStore = useMainStore();

      user.$reset();
      globalStore.$reset();
      mainStore.$reset();
      AtToastPlugin.error({
        title: e.response?.data.message || i18n.global.t('app.unknownError'),
        duration: 6000,
      });
      return Promise.reject(e);
    }

    if (status === 403) {
      router.replace({ name: ROUTE_NAMES.login });

      const user = useUserStore();
      const globalStore = useNasiyaGlobalStore();
      const mainStore = useMainStore();

      user.$reset();
      globalStore.$reset();
      mainStore.$reset();

      const message = e.response.data.message;

      if (message.includes('Expired token') || message.includes('Wrong number of segments')) {
        AtToastPlugin.error({
          title: i18n.global.t('app.loginAgainRequired'),
          duration: 4000,
        });
      } else {
        AtToastPlugin.error({
          title: message || i18n.global.t('app.unknownError'),
          duration: 4000,
        });
      }

      return Promise.reject(e);
    }

    return Promise.reject(e);
  }
);

export default apiClient;
